import React, { FC, useState, useRef } from 'react';
import { CrewMember, TimeEntryInput } from '@bechtel/types';
import { FastField, useFormikContext, useField } from 'formik';
import { TotalCellStyled } from '~/detail/styled';
import { Icon, Text, IconSize, Popover, Box } from '@bechtel/gds';
import { TimeEntryCellStyled, HourTextFieldStyled, CommentsBoxStyled } from './styled';
import { find, get, isNumber } from 'lodash';
import { AddCommentFormikState, AddCommentSidesheet } from '~/components';
import { useTimesheet } from '~/detail/context';
import { roundedHoursValue } from '~/detail/utils';

export interface TimeEntryProps {
  timeEntryInput: TimeEntryInput;
  formikParentFieldName: string;
}
export const TimeEntry: FC<TimeEntryProps> = ({ timeEntryInput, formikParentFieldName }) => {
  const [show, setShow] = useState<boolean>(false);
  const { values, setFieldValue } = useFormikContext();
  const commentsAvailable = !!get(values, `${formikParentFieldName}.commentId`);
  const [showAddCommentSidesheet, setsShowAddCommentSidesheet] = useState(false);
  const { crewEmployees, isEditable, roundingIncrement } = useTimesheet();
  const [field, meta, helpers] = useField(`${formikParentFieldName}.hours`);
  const { setValue, setTouched } = helpers;
  const hoursFieldError = meta.touched && meta.error ? true : false;
  const triggerElementRef = useRef<HTMLDivElement>(null);

  const closeSidesheet = () => {
    setsShowAddCommentSidesheet(false);
  };

  const crewMember = find(crewEmployees, (member) => member.id === timeEntryInput.crewMemberId);

  const onSubmit = (values: AddCommentFormikState) => {
    setFieldValue(`${formikParentFieldName}`, { ...timeEntryInput, ...values });
    closeSidesheet();
  };

  return (
    <>
      <TotalCellStyled>
        <TimeEntryCellStyled
          hasError={hoursFieldError}
          ref={triggerElementRef}
          onMouseLeave={() => setShow(false)}
          onClick={() => setShow(true)}
        >
          <HourTextFieldStyled
            autoComplete="off"
            type="number"
            min={0}
            max={8}
            {...field}
            placeholder="0.0"
            maxLength={5}
            disabled={!isEditable}
            onChange={(e) => {
              setTouched(true);
              setValue(e.target.value ? roundedHoursValue(Number(e.target.value.trim()), roundingIncrement) : '');
            }}
          />
          <FastField name={`${formikParentFieldName}.commentId`} value={''} type="hidden"></FastField>
          <FastField name={`${formikParentFieldName}.otherCommentDescription`} value={''} type="hidden"></FastField>
          <CommentsBoxStyled
            onClick={() => setsShowAddCommentSidesheet(true)}
            disabled={!isNumber(timeEntryInput.hours) || !isEditable}
          >
            <Icon
              iconStyle="solid"
              fillColor={commentsAvailable ? 'green.500' : 'grey.500'}
              size={IconSize.MEDIUM}
              icon="comment-alt"
            ></Icon>
            <Text size="200" color="grey.1000">
              {commentsAvailable ? '01' : '00'}
            </Text>
          </CommentsBoxStyled>
        </TimeEntryCellStyled>
        <Popover
          triggerComponentRef={triggerElementRef}
          placement="right"
          showPopper={show && hoursFieldError}
          customStyle={{ zIndex: 2, top: '-40px' }}
        >
          <Box
            px={{ sm: '400' }}
            py={{ sm: '400' }}
            style={{ backgroundColor: 'red', borderRadius: '6px', cursor: 'pointer' }}
            onClick={() => setShow(false)}
            justifyContent={{ sm: 'center' }}
            alignItems={{ sm: 'center' }}
          >
            <Text style={{ whiteSpace: 'nowrap' }} family="graphik" size="300" color="white">
              {meta.error}&nbsp;
            </Text>
            <Icon icon="xmark" fillColor="white" size={IconSize.MEDIUM}></Icon>
          </Box>
        </Popover>
      </TotalCellStyled>

      {showAddCommentSidesheet && (
        <AddCommentSidesheet
          crewMember={crewMember as CrewMember}
          closeSidesheet={closeSidesheet}
          onSubmit={onSubmit}
          timeEntryInput={timeEntryInput}
        />
      )}
    </>
  );
};
